'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import DashboardHeader from '@/app/components/DashboardHeader';
import MetricsBentoGrid from '@/app/components/MetricsBentoGrid';
import SubmissionsChart from '@/app/components/SubmissionsChart';
import RecentSubmissionsTable from '@/app/components/RecentSubmissionsTable';
import RemindersPanel from '@/app/components/RemindersPanel';
import AlertsPanel from '@/app/components/AlertsPanel';
import { createClient } from '@/lib/supabase/client';

export interface SubmissionRow {
  id: string;
  studentName: string;
  trn: string;
  trn4: string;
  email: string;
  mailingAddress: string;
  telephone: string;
  programme: string;
  cohort: string;
  submissionDate: string;
  submissionStatus: 'submitted' | 'under_review' | 'verified' | 'rejected' | 'incomplete' | 'resubmission_required';
  docsTotal: number;
  docsVerified: number;
  docsRejected: number;
  followUp: boolean;
  docUploadStatus: string;
}

export interface DashboardMetrics {
  totalStudents: number;
  completeSubmissions: number;
  pendingReview: number;
  rejectedDocs: number;
  incompleteSubmissions: number;
  followUpRequired: number;
}

export interface ProgrammeChartData {
  programme: string;
  complete: number;
  incomplete: number;
  pending: number;
}

// Staff roles that can access the admin dashboard
const ADMIN_ROLES = ['admin', 'manager', 'assessment_manager', 'reviewer'];

export default function AdminDashboardPageClient() {
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalStudents: 0,
    completeSubmissions: 0,
    pendingReview: 0,
    rejectedDocs: 0,
    incompleteSubmissions: 0,
    followUpRequired: 0,
  });
  const [submissions, setSubmissions] = useState<SubmissionRow[]>([]);
  const [programmeData, setProgrammeData] = useState<ProgrammeChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [authChecking, setAuthChecking] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Auth guard — allow admin, manager, assessment_manager, reviewer
  useEffect(() => {
    const checkAuth = async () => {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        window.location.href = '/admin-sign-in';
        return;
      }
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      // Allow by role or by known staff email fallback
      const role = profile?.role || user.user_metadata?.role;
      const knownStaffEmails = ['admin@zivasol.com', 'assessment@zivasol.com', 'christall.byfield@zivasol.com'];
      const isStaff = ADMIN_ROLES.includes(role) || knownStaffEmails.includes(user.email || '');

      if (!isStaff) {
        window.location.href = '/admin-sign-in';
        return;
      }
      setAuthChecking(false);
    };
    checkAuth();
  }, []);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();

    try {
      // Fetch student submissions with user profile data
      const { data: submissionsData, error: subError } = await supabase
        .from('student_submissions')
        .select(`
          id,
          programme,
          cohort,
          trn4,
          overall_status,
          submitted_date,
          created_at,
          biodata_data,
          student_id,
          user_profiles!student_submissions_student_id_fkey (
            id,
            full_name,
            email,
            phone
          )
        `)
        .order('created_at', { ascending: false });

      if (subError) {
        console.error('Error fetching submissions:', subError);
      }

      // Fetch document records for counts
      const { data: docData, error: docError } = await supabase
        .from('document_records')
        .select('id, student_id, submission_id, doc_status');

      if (docError) {
        console.error('Error fetching documents:', docError);
      }

      // Fetch total student count (role = student)
      const { count: studentCount } = await supabase
        .from('user_profiles')
        .select('id', { count: 'exact', head: true })
        .eq('role', 'student');

      const docs = docData || [];
      const subs = submissionsData || [];

      // Build doc counts per submission
      const docsBySubmission: Record<string, { total: number; verified: number; rejected: number; pending: number }> = {};
      docs.forEach((d) => {
        const sid = d.submission_id || d.student_id;
        if (!sid) return;
        if (!docsBySubmission[sid]) docsBySubmission[sid] = { total: 0, verified: 0, rejected: 0, pending: 0 };
        docsBySubmission[sid].total++;
        if (d.doc_status === 'verified') docsBySubmission[sid].verified++;
        if (d.doc_status === 'rejected' || d.doc_status === 'resubmission_required') docsBySubmission[sid].rejected++;
        if (d.doc_status === 'under_review' || d.doc_status === 'uploaded') docsBySubmission[sid].pending++;
      });

      // Map submissions to SubmissionRow
      const mappedSubmissions: SubmissionRow[] = subs.map((s: any) => {
        const profile = Array.isArray(s.user_profiles) ? s.user_profiles[0] : s.user_profiles;
        const docCounts = docsBySubmission[s.id] || { total: 0, verified: 0, rejected: 0, pending: 0 };
        const biodata = s.biodata_data || {};

        // Map overall_status to submission status
        let submissionStatus: SubmissionRow['submissionStatus'] = 'incomplete';
        if (s.overall_status === 'complete') submissionStatus = 'verified';
        else if (s.overall_status === 'under_review') submissionStatus = 'under_review';
        else if (s.overall_status === 'action_required') submissionStatus = 'resubmission_required';
        else if (s.overall_status === 'pending') submissionStatus = docCounts.total > 0 ? 'submitted' : 'incomplete';
        else submissionStatus = 'incomplete';

        const docUploadStatus = docCounts.total === 0
          ? 'No documents'
          : `${docCounts.verified}/${docCounts.total} verified`;

        return {
          id: s.id,
          studentName: profile?.full_name || biodata?.fullName || 'Unknown Student',
          trn: biodata?.trn || '',
          trn4: s.trn4 || biodata?.trn?.slice(-4) || '****',
          email: profile?.email || biodata?.email || '',
          mailingAddress: biodata?.mailingAddress || biodata?.address || '',
          telephone: profile?.phone || biodata?.telephone || biodata?.phone || '',
          programme: s.programme || '',
          cohort: s.cohort || '',
          submissionDate: s.submitted_date || (s.created_at ? new Date(s.created_at).toLocaleDateString('en-GB') : ''),
          submissionStatus,
          docsTotal: docCounts.total,
          docsVerified: docCounts.verified,
          docsRejected: docCounts.rejected,
          followUp: s.overall_status === 'action_required' || docCounts.rejected > 0,
          docUploadStatus,
        };
      });

      // Compute metrics from live data only — no dummy values
      const totalStudents = studentCount || subs.length;
      const completeSubmissions = subs.filter((s: any) => s.overall_status === 'complete').length;
      const pendingReview = subs.filter((s: any) => s.overall_status === 'under_review' || s.overall_status === 'pending').length;
      const rejectedDocs = docs.filter((d) => d.doc_status === 'rejected' || d.doc_status === 'resubmission_required').length;
      const incompleteSubmissions = subs.filter((s: any) => s.overall_status === 'action_required').length;
      const followUpRequired = mappedSubmissions.filter((s) => s.followUp).length;

      setMetrics({
        totalStudents,
        completeSubmissions,
        pendingReview,
        rejectedDocs,
        incompleteSubmissions,
        followUpRequired,
      });

      setSubmissions(mappedSubmissions);

      // Build programme chart data from live submissions only
      const progMap: Record<string, { complete: number; incomplete: number; pending: number }> = {};
      subs.forEach((s: any) => {
        const prog = s.programme || 'Unknown';
        const shortProg = prog.length > 30 ? prog.substring(0, 28) + '…' : prog;
        if (!progMap[shortProg]) progMap[shortProg] = { complete: 0, incomplete: 0, pending: 0 };
        if (s.overall_status === 'complete') progMap[shortProg].complete++;
        else if (s.overall_status === 'under_review') progMap[shortProg].pending++;
        else progMap[shortProg].incomplete++;
      });

      const chartData: ProgrammeChartData[] = Object.entries(progMap).map(([programme, counts]) => ({
        programme,
        ...counts,
      }));

      setProgrammeData(chartData);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authChecking) fetchDashboardData();
  }, [fetchDashboardData, authChecking]);

  if (authChecking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
      </div>
    );
  }

  return (
    <AppLayout activePath="/admin-dashboard">
      <div className="min-h-screen bg-background">
        <DashboardHeader onRefresh={fetchDashboardData} lastUpdated={lastUpdated} loading={loading} submissions={submissions} />
        <div className="px-6 lg:px-8 xl:px-10 2xl:px-12 pb-10 max-w-screen-2xl mx-auto">
          <MetricsBentoGrid metrics={metrics} loading={loading} />
          <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
            <div className="xl:col-span-2 2xl:col-span-3 space-y-6">
              <SubmissionsChart programmeData={programmeData} loading={loading} />
              <RecentSubmissionsTable submissions={submissions} loading={loading} onRefresh={fetchDashboardData} />
            </div>
            <div className="xl:col-span-1 space-y-5">
              <AlertsPanel />
              <RemindersPanel />
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
